import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { LanguageSwitcher } from '../layout/LanguageSwitcher';
import { useAuth } from './AuthContext';
import { passkeysSupported } from './api';

// Error codes the backend is known to return. Anything else falls back to a
// generic message rather than showing a raw code to the user.
const KNOWN_ERRORS = new Set([
  'invalid_credentials',
  'account_locked',
  'too_many_attempts',
  'mfa_invalid_code',
  'mfa_challenge_invalid',
  'passkey_refused',
  'passkey_needs_email',
  'network',
]);

function messageFor(error: unknown): string {
  const code = error instanceof ApiError ? error.code : 'unknown';
  return KNOWN_ERRORS.has(code) ? code : 'unknown';
}

export function LoginPage() {
  const { t } = useTranslation();
  const { signIn, pendingMfa, submitMfa, cancelMfa, signInWithPasskey } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function attempt(action: () => Promise<void>) {
    setErrorCode(null);
    setBusy(true);
    try {
      await action();
    } catch (error: unknown) {
      setErrorCode(messageFor(error));
    } finally {
      setBusy(false);
    }
  }

  const handlePassword = (event: FormEvent) => {
    event.preventDefault();
    void attempt(() => signIn(email, password));
  };

  const handleCode = (event: FormEvent) => {
    event.preventDefault();
    void attempt(async () => {
      await submitMfa(code, useRecovery ? 'recovery' : 'totp');
      setCode('');
    });
  };

  const handlePasskey = () => {
    const address = email.trim();

    return void attempt(async () => {
      try {
        // With an address the server lists that account's keys, so any key works
        // — including one that does not store the account inside itself. Without
        // one, only a discoverable credential can answer.
        await signInWithPasskey(address || null);
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'NotAllowedError') {
          // The specification makes "you cancelled" and "no key here matches"
          // deliberately indistinguishable, so that a stranger cannot learn
          // whether an account exists. The message therefore has to cover both
          // — and when no address was given, the likely cause is a key running
          // in U2F mode, which can only be offered once the account is known.
          setErrorCode(address ? 'passkey_refused' : 'passkey_needs_email');
          return;
        }
        throw error;
      }
    });
  };

  const error = errorCode && (
    <p className="login__error" role="alert">
      {t(`auth.errors.${errorCode}`)}
    </p>
  );

  return (
    <main className="login">
      <header className="login__header">
        <h1>{t('app.name')}</h1>
        <p>{t('app.tagline')}</p>
      </header>

      {pendingMfa ? (
        <form className="login__form" onSubmit={handleCode}>
          <h2>{t('auth.mfa.title')}</h2>
          <p className="login__hint">
            {useRecovery ? t('auth.mfa.recoveryHint') : t('auth.mfa.hint')}
          </p>

          <label htmlFor="code">
            {useRecovery ? t('auth.mfa.recoveryCode') : t('auth.mfa.code')}
          </label>
          <input
            id="code"
            // A one-time code is digits, and a numeric keypad on a phone is the
            // difference between a two-second login and a fiddly one.
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoFocus
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />

          {error}

          <button type="submit" disabled={busy}>
            {busy ? t('auth.signingIn') : t('auth.submit')}
          </button>

          <div className="login__links">
            <button
              type="button"
              className="link"
              onClick={() => {
                setUseRecovery((current) => !current);
                setCode('');
                setErrorCode(null);
              }}
            >
              {useRecovery ? t('auth.mfa.useApp') : t('auth.mfa.useRecovery')}
            </button>
            <button
              type="button"
              className="link"
              onClick={() => {
                cancelMfa();
                setCode('');
                setErrorCode(null);
              }}
            >
              {t('common.back')}
            </button>
          </div>
        </form>
      ) : (
        <form className="login__form" onSubmit={handlePassword}>
          <h2>{t('auth.signIn')}</h2>

          <label htmlFor="email">{t('auth.email')}</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <label htmlFor="password">{t('auth.password')}</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {error}

          <button type="submit" disabled={busy}>
            {busy ? t('auth.signingIn') : t('auth.submit')}
          </button>

          {passkeysSupported() && (
            <div className="login__links">
              <button type="button" className="link" onClick={handlePasskey} disabled={busy}>
                {t('auth.passkey.signIn')}
              </button>
              <span className="login__hint">{t('auth.passkey.hint')}</span>
            </div>
          )}
        </form>
      )}

      <footer className="login__footer">
        <LanguageSwitcher />
      </footer>
    </main>
  );
}
